<template>
<q-page class="main">
  <q-card class="page-card">
    <q-card-section class="page-card__main-content">
      <item-header :item="rule" :reference-number="rule.code">
        <template v-slot:header>
          <h1 class="heading-2 q-mb-lg">{{ ruleVersion.summary }}</h1>
        </template>
        <template v-slot:type>
          <q-chip v-if="rule.type" :label="$ruleTypeLabel(rule.type)" class="type-chip" outline />
        </template>
      </item-header>
      <common-banner v-if="rule.deleted_at" type="warning" class="q-mb-lg">
        Deze richtlijn is gearchiveerd en kan niet worden gevonden via de zoektool.
        <template v-slot:actions>
          <q-btn v-if="$can('destroy', rule)"
                 color="warning" text-color="text-base" class="q-mr-sm" unelevated no-caps @click="restoreRule">
            Herstel richtlijn
          </q-btn>
          <q-btn v-if="$can('delete-from-archive')"
                 color="negative" text-color="white" unelevated no-caps @click="forceDeleteRule">
            Verwijder defintief
          </q-btn>
        </template>
      </common-banner>
      <common-banner v-else-if="isOldVersion" type="warning" class="q-mb-lg">
        Deze versie van de richtlijn is verouderd.
        <template v-slot:actions>
          <common-link :href="$route('rule.show', rule.id)"
                       label="Naar de huidige versie" />
        </template>
      </common-banner>
      <common-banner v-else-if="!ruleVersion.is_published" class="q-mb-lg">
        Deze versie van de richtlijn is in concept en kan niet worden gevonden via de zoektool.
        <template v-slot:actions
                  v-if="$can('publish', rule)">
          <q-btn class="q-mr-sm" color="primary" text-color="white" unelevated no-caps @click="publishRule">
            Publiceer versie
          </q-btn>
          <q-btn v-if="isNotThePublishedVersion"
                 color="primary"
                 outline
                 unelevated
                 no-caps
                 @click="$goto('rule.show_version',{rule_id: rule.id, version_id: publishedVersion.id})">
            Huidige versie
          </q-btn>
        </template>
      </common-banner>
      <div class="item__info-table" v-if="leadingCategory?.content_owner">
        <div>
          <strong>Contenteigenaar/eigenaren</strong>
        </div>
        <div>
          <span>{{ leadingCategory.content_owner }}</span>
        </div>
      </div>
      <div class="item__info-table q-mb-md">
        <div>
          <strong>Bewerkers</strong>
        </div>
        <div v-if="ruleVersionUsers">
          <span class="q-mr-lg">{{ ruleVersionUsers }}</span>
        </div>
        <span v-else>
                    -
                    </span>
      </div>
      <template v-if="ruleVersion.motivation">
        <h4 class="q-mb-md">{{ fieldLabels.motivation }}</h4>
        <p class="editor-styling" v-html="ruleVersion.motivation" />
        <h4 class="q-mb-md" v-if="ruleVersion.description">{{ fieldLabels.possible_solution }}</h4>
        <p class="editor-styling" v-html="ruleVersion.description" />
      </template>
      <template v-else>
        <h4 class="q-mb-md">{{  fieldLabels.description }}</h4>
        <p class="editor-styling" v-html="ruleVersion.description" />
      </template>
    </q-card-section>
    <q-card-section class="page-card__details">
      <expansion-element
          label="Documenten"
          :items-count="ruleVersion.assets.length"
          showEmpty>
        <document-list
            :documents="ruleVersion.assets"
            :uploadUrl="!ruleVersion.is_published
                          ? $route('rule.add_file', [ruleVersion.rule_id, ruleVersion.id])
                          : null"
            :deleteUrl="!ruleVersion.is_published
                          ? $route('rule.delete_file',  [ruleVersion.rule_id, ruleVersion.id])
                          : null"
            :can-edit="!ruleVersion.is_published && $can('addFiles', rule)"
        />
        <template v-if="ruleVersion.is_published && $can('addFiles', rule)" v-slot:actions>
          <i>Bestanden van een gepubliceerde richtlijnversie kunnen niet worden aangepast</i>
          <flat-button label="Nieuwe versie aanmaken"
                       icon="edit"
                       class="q-ml-md"
                       @click="$goto('rule.new_version', ruleVersion.rule_id)" />
        </template>
      </expansion-element>
      <expansion-element
          label="Externe links"
          :items-count="ruleVersion.links?.length || 0"
      >
        <q-list bordered separator class="rounded-borders">
          <q-item v-for="link in ruleVersion.links" :key="link.id" class="items-center">
            <q-item-section>
              <common-link :label="link.name || link.href" :href="link.href" target="_blank" />
            </q-item-section>
          </q-item>
        </q-list>
      </expansion-element>
      <expansion-element
          label="Van toepassing op fase(s)"
          :items-count="ruleVersion.phases.length"
      >
        <chip-list :items="ruleVersion.phases" />
      </expansion-element>
      <expansion-element
          label="Categorie&euml;n"
          :items-count="ruleVersion.categories.length"
      >
        <chip-list :items="categories" :leading-item="leadingCategory" />
      </expansion-element>
      <expansion-element v-if="ruleVersion.subcategories.length"
                         label="Subcategorie&euml;en"
                         :items-count="ruleVersion.subcategories.length"
      >
        <chip-list :items="ruleVersion.subcategories" />
      </expansion-element>
      <expansion-element v-if="ruleVersion.themes.length"
                         label="Thema's"
                         :items-count="ruleVersion.themes.length"
      >
        <chip-list :items="ruleVersion.themes" />
      </expansion-element>
    </q-card-section>
  </q-card>
  <sticky-top-bar>
    <template v-if="canArchiveRule" v-slot:menu_items>
      <nav-list-item
          label="Archiveren"
          icon="inventory_2"
          action
          @click="onArchive"
      />
    </template>
  </sticky-top-bar>
  <sticky-bottom-bar>
    <template v-if="$isDesktop">
      <common-fab v-if="canEditStandardRule" icon="edit" label="Bewerken" @click="editRule" />
    </template>
    <common-fab v-else-if="canEditStandardRule || canArchiveRule">
      <template v-slot:actions>
        <fab-action
            v-if="canEditStandardRule"
            icon="edit"
            label="Bewerken"
            @click="editRule"
        />
        <fab-action
            v-if="canArchiveRule"
            icon="archive"
            label="Archiveren"
            @click="onArchive"
        />
      </template>
    </common-fab>
  </sticky-bottom-bar>
</q-page>
</template>

<script>
import ChipList from '~HIOR/components/lists/ChipList'
import DocumentList from '~HIOR/components/lists/DocumentList'
import PhaseList from '~HIOR/components/lists/PhaseList'
import { ruleMixin } from '~HIOR/mixins'
import { ruleType } from '~HIOR/constants'
import { promiseTimeout } from '@vueuse/core'
import { config } from '~INSTANCE'

export default {
  mixins: [ruleMixin],
  components: {
    ChipList,
    DocumentList,
    PhaseList,
  },
  props: {
    rule: {
      type: Object,
      default: null,
    },
    ruleVersion: {
      type: Object,
      default: null,
    },
  },
  computed: {
    leadingCategory () {
      return this.rule.leading_category_id ? this.rule.leading_category : null
    },
    categories () {
      if (this.rule.leading_category_id) {
        this.ruleVersion.categories.filter((category) => {
          return category.id !== this.rule.leading_category_id
        })
      }

      return this.ruleVersion.categories
    },
    ruleVersionUsers () {
      if (!this.ruleVersion.users) {
        return
      }

      const users = this.ruleVersion.users.map((user) => {
        return this.$fullName(user)
      })

      return users.join(', ')
    },
    canEditStandardRule () {
      return !this.rule.deleted_at && this.rule.type !== ruleType.TYPE_CUSTOM && this.$can('edit', this.rule)
    },
    canArchiveRule () {
      return !this.rule.deleted_at && this.$can('destroy', this.rule)
    },
    isOldVersion () {
      return this.rule.current_version.is_published && this.rule.current_version.id !== this.ruleVersion.id
    },
    publishedVersion () {
      return [...this.rule.versions].reverse().find(version => version.is_published)
    },
    isNotThePublishedVersion () {
      return !this.publishedVersion ||
          !this.ruleVersion.is_published ||
          this.publishedVersion.id !== this.ruleVersion.id
    },
    fieldLabels () {
      return {
        description: config.translations?.rule?.description ?? 'Omschrijving',
        motivation: config.translations?.rule?.motivation ?? 'Motivatie',
        possible_solution: config.translations?.rule?.possible_solution ?? 'Mogelijke oplossing',
      }
    },
  },
  methods: {
    editRule () {
      this.$goto('rule.edit', { rule_id: this.rule.id, version_id: this.ruleVersion.id })
    },
    restoreRule () {
      this.$goto('rule.restore', { rule_id: this.rule.id })
    },
    publishRule () {
      this.$goto('rule.publish_version', { rule_id: this.rule.id, version_id: this.ruleVersion.id })
    },
    async onArchive () {
      if (await this.$confirm(
          {
            title: 'Richtlijn archiveren',
            messages: ['Weet u zeker dat u deze regel wilt archiveren?'],
          },
      )) {
        try {
          await axios.delete(this.$route('rule.destroy', { rule_id: this.rule.id }))
          this.$toast('success', 'De richtlijn is verwijderd')
        } catch (e) {
          console.error(e)
          this.$toast('error', 'De richtlijn kon niet worden verwijderd')
          return
        }

        await promiseTimeout(1000)
        this.$goto('rule.index')
      }
    },
    async forceDeleteRule () {
      if (!await this.$confirm(
          {
            title: 'Richtlijn vernietigen',
            messages: ['Weet u zeker dat u deze richtlijn definitief wilt verwijderen? Deze richtlijn en alle versies ervan worden verwijderd en losgekoppeld van alle projecten waar deze aan verbonden was. Deze actie kan niet worden teruggedraaid.'],
          },
      )) {
        return
      }
      try {
        await axios.delete(this.$route('rule.force-delete', { rule_id: this.rule.id }))
        this.$toast('success', 'De richtlijn is verwijderd')
      } catch (error) {
        console.error(error)
        this.$toast('error', 'Er is iets mis gegaan')
        return
      }
      await promiseTimeout(1000)
      this.$goto('rule.index')
    },
  },
}
</script>
